/**
 * Client Event Tracker
 *
 * Buffers client-side events and flushes them to Weaver's
 * /rpc/clientEvents/ingest endpoint in batches.
 *
 * Designed to be held as a singleton (via Jotai atom) with deps that
 * can be updated as auth/config becomes available. The trace ID is
 * stable for the lifetime of the instance (one per page load).
 *
 * Usage:
 *   // From Jotai action atoms:
 *   const tracker = get(eventTrackerAtom);
 *   tracker?.track('recording_started');
 *
 *   // From React components:
 *   const tracker = useAtomValue(eventTrackerAtom);
 *   tracker?.track('template_selected', { template_id: 'soap' });
 */

import { createDebugLogger } from '@videa/utils';

import type { AmbientClientDependencies } from './ambientClient';

const debug = createDebugLogger('EventTracker');

// Must stay in sync with CLIENT_EVENT_TYPES in weaver's schema
export type ClientEventType =
  | 'app_initialized'
  | 'appointment_selected'
  | 'template_selected'
  | 'recording_blocked_by_other_tab'
  | 'recording_started'
  | 'recording_paused'
  | 'recording_resumed'
  | 'recording_canceled'
  | 'recording_stopped'
  | 'chunk_upload_started'
  | 'chunk_upload_success'
  | 'chunk_upload_failed'
  | 'connection_lost'
  | 'connection_restored'
  | 'note_generation_requested'
  | 'note_generation_received';

interface BufferedEvent {
  sessionId: string | null;
  eventType: ClientEventType;
  eventTimestamp: string;
  metadata: Record<string, unknown>;
}

export interface EventTrackerConfig {
  /** Client app version for debugging */
  clientVersion?: string;
  /** Flush interval in ms (default: 10000) */
  flushIntervalMs?: number;
  /** Max events per flush batch (default: 500) */
  maxBatchSize?: number;
  /** Max buffered events; oldest are dropped when exceeded to prevent unbounded growth on flush failures (default: 10000) */
  maxBufferSize?: number;
}

export interface EventTracker {
  /** Track a client event, optionally with metadata */
  track: (
    eventType: ClientEventType,
    metadata?: Record<string, unknown>,
  ) => void;
  /** Set the current session ID (call when a recording session starts) */
  setSessionId: (id: string) => void;
  /** Clear the current session ID (call when a recording session ends) */
  clearSessionId: () => void;
  /** Update the API dependencies (call when auth/config becomes available or changes) */
  updateDeps: (deps: AmbientClientDependencies) => void;
  /** Force an immediate flush (e.g., before session completion) */
  flush: () => Promise<void>;
  /** Get the client trace ID for this app instance */
  getTraceId: () => string;
  /** Cleanup: flush remaining events and stop the interval */
  dispose: () => Promise<void>;
}

/**
 * No-op EventTracker used when Voice Notes (and thus event tracking) is disabled.
 * Same interface as the real tracker; all methods are no-ops so callers need no null checks.
 */
export const noopEventTracker: EventTracker = {
  track: () => {},
  setSessionId: () => {},
  clearSessionId: () => {},
  updateDeps: () => {},
  flush: async () => {},
  getTraceId: () => '',
  dispose: async () => {},
};

/** Generate a UUID v4. Uses crypto.randomUUID() when available (browsers, Node 19+), else a fallback for test envs (e.g. Jest/jsdom). */
function generateTraceId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof (crypto as Crypto & { randomUUID?: () => string }).randomUUID ===
      'function'
  ) {
    return (crypto as Crypto & { randomUUID: () => string }).randomUUID();
  }
  // Fallback for environments without randomUUID (e.g. older Node, some test runners)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function createEventTracker(
  config: EventTrackerConfig = {},
): EventTracker {
  const {
    clientVersion,
    flushIntervalMs = 10_000,
    maxBatchSize = 500,
    maxBufferSize = 10_000,
  } = config;

  const clientTraceId = generateTraceId();
  let sessionId: string | null = null;
  let deps: AmbientClientDependencies | null = null;
  const buffer: BufferedEvent[] = [];
  let disposed = false;
  let isFlushing = false;

  // Periodic flush
  const intervalId = setInterval(() => {
    flushInternal().catch(logFlushError);
  }, flushIntervalMs);

  // Flush on page visibility change (tab switch, app background)
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      flushInternal().catch(logFlushError);
    }
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // TODO: Consider sendBeacon for beforeunload as a future improvement.
  // sendBeacon can't include Authorization headers, so we'd need either:
  //   (a) an unauthenticated ingest endpoint on Weaver (e.g. /rpc/clientEvents/beacon)
  //   (b) embedding a short-lived token in the JSON body
  // For now, we rely on visibilitychange (which fires on tab switch, app
  // background, and most navigation) and accept that the last ≤10s of
  // events may be lost on a hard tab close.

  function logFlushError(err: unknown) {
    debug.warn('flush failed:', err);
  }

  async function flushInternal(): Promise<void> {
    if (buffer.length === 0 || disposed || !deps) return;
    if (isFlushing) return;

    isFlushing = true;
    const batch = buffer.splice(0, maxBatchSize);

    try {
      const token = await deps.getSynapseToken();
      const baseUrl = deps.synapseApiUrl.replace(/\/$/, '');
      const response = await fetch(`${baseUrl}/rpc/clientEvents/ingest`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Organization-Id': deps.organizationId,
          'X-Practice-Id': deps.practiceId,
        },
        body: JSON.stringify({
          json: {
            clientTraceId,
            clientVersion,
            events: batch,
          },
        }),
      });

      if (!response.ok) {
        buffer.unshift(...batch);
        debug.warn('flush failed:', response.status);
      }
    } catch {
      buffer.unshift(...batch);
    } finally {
      // eslint-disable-next-line require-atomic-updates -- intentional guard: only one flush runs at a time; finally always releases
      isFlushing = false;
    }
  }

  function track(
    eventType: ClientEventType,
    metadata?: Record<string, unknown>,
  ) {
    if (disposed) return;

    buffer.push({
      sessionId,
      eventType,
      eventTimestamp: new Date().toISOString(),
      metadata: metadata ?? {},
    });

    // Cap buffer size to prevent unbounded growth when flush repeatedly fails
    if (buffer.length > maxBufferSize) {
      const dropCount = buffer.length - maxBufferSize;
      buffer.splice(0, dropCount);
      debug.warn(
        `buffer exceeded maxBufferSize (${maxBufferSize}), dropped ${dropCount} oldest event(s)`,
      );
    }
  }

  function setSessionId(id: string) {
    sessionId = id;
  }

  function clearSessionId() {
    sessionId = null;
  }

  function updateDeps(newDeps: AmbientClientDependencies) {
    deps = newDeps;
  }

  async function flush() {
    await flushInternal();
  }

  function getTraceId() {
    return clientTraceId;
  }

  async function dispose() {
    clearInterval(intervalId);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    await flushInternal();
    disposed = true;
  }

  // Track initialization as the first event
  track('app_initialized');

  return {
    track,
    setSessionId,
    clearSessionId,
    updateDeps,
    flush,
    getTraceId,
    dispose,
  };
}
